import React from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { FaExternalLinkAlt } from 'react-icons/fa';

import { useAppSelector } from '@/store';
import { authStore } from '@/store/auth';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';

import { pages } from '@/settings/pages';
import { auth } from '@/constants';
import { Dropdown } from '@/components/shared/Dropdown';

import * as styles from './Style.module.scss';

type Item = {
  dataTest: string;
  onClick?: () => void;
  label: React.ReactNode;
  href?: string;
  externalLink?: boolean;
};

export const LoginMenu: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const gloplaId = useAppSelector(authStore.selectors.gloplaIdSelector);
  const { enabled: isRoleListEnabled } = useFeatureFlag('product_admin_roles');

  const linkTo =
    (to: string, withLocation = false) =>
    () => {
      if (withLocation) {
        navigate(to, { state: { from: location } });
      } else {
        navigate(to);
      }
    };
  const openOrganizationSelectPage = async () => {
    // 下記pathへ遷移することで、auth側のOIDCフローの処理が行われ、組織選択画面へ遷移します
    window.location.href = auth.Url.signinUri;
  };

  const items: Item[] = [
    {
      // Home
      dataTest: 'home',
      onClick: linkTo(pages.top),
      href: pages.top,
      label: t('pages.home.title'),
    },
    {
      // 管理者一覧
      dataTest: 'memberships',
      onClick: linkTo(pages.membership.index),
      href: pages.membership.index,
      label: t('pages.membership.index.title'),
    },
    ...(isRoleListEnabled
      ? [
          {
            // フィーチャーフラグがONの場合のみサービス権限一覧を追加
            dataTest: 'roles',
            onClick: linkTo(pages.role.list),
            href: pages.role.list,
            label: t('pages.role.list.title'),
          },
        ]
      : []),
    {
      // アカウント情報
      dataTest: 'account',
      onClick: linkTo(pages.account.index),
      href: pages.account.index,
      label: t('pages.account.index.title'),
    },
    {
      // 所属組織の選択
      dataTest: 'organizations',
      onClick: () => {
        openOrganizationSelectPage();
      },
      label: t('pages.organizations.title'),
    },
    {
      // 操作ガイド・よくある質問
      dataTest: 'faq',
      label: (
        <>
          {t('pages.support.faq.title')}
          <FaExternalLinkAlt className={styles.externalIcon} />
        </>
      ),
      href: t('pages.support.faq.url'),
      externalLink: true,
    },
    {
      // ログアウト
      dataTest: 'logout-menu',
      onClick: linkTo(pages.signout.index),
      href: pages.signout.index,
      label: t('ui.action.logout'),
    },
  ];

  if (!gloplaId) return null;

  return (
    <Dropdown
      dataTest="dropdown-menu"
      buttonClassNames={styles.button}
      text={t('ui.menus.menu')}
      menuWrapperClassNames={styles.menuWrapper}
      menuNode={
        <>
          {items.map((item, index) =>
            item.externalLink ? (
              <a
                data-test={item.dataTest}
                className={styles.menuItem}
                key={index}
                target="_blank"
                href={item.href}
              >
                {item.label}
              </a>
            ) : (
              <div
                data-test={item.dataTest}
                className={styles.menuItem}
                key={index}
                onClick={item.onClick}
              >
                {item.label}
              </div>
            ),
          )}
        </>
      }
    />
  );
};
